/** @format */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaGift } from "react-icons/fa";
import { Link } from "react-router-dom";
import { devise, TruncateText } from "../../utils/utils";
import Ratings from "../Ratings";

const RecommendedProducts = ({
  products = [],
  onAddToCart,
  onAddToWishlist,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -340 : 340,
      behavior: "smooth",
    });
  };

  const getDiscountedPrice = (price, discount) =>
    Math.floor((parseFloat(price) * (100 - discount)) / 100);

  if (!products?.length) return null;

  return (
    <div className='relative'>
      {/* Scroll buttons */}
      <button
        onClick={() => scroll("left")}
        className='absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-gray-500 hover:text-gray-800 transition-all'
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => scroll("right")}
        className='absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-gray-500 hover:text-gray-800 transition-all'
      >
        <ChevronRight size={18} />
      </button>

      {/* Scrollable list */}
      <div
        ref={scrollRef}
        className='flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2'
        style={{ scrollbarWidth: "none" }}
      >
        {products.map((product, i) => {
          const finalPrice = product.discount
            ? getDiscountedPrice(product.price, product.discount)
            : product.price;

          return (
            <Link
              key={product._id || i}
              to={`/product/details/${product._id}`}
              className='group flex-shrink-0 flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 p-3 w-[300px] sm:w-[320px]'
            >
              {/* Image */}
              <div className='relative flex-shrink-0'>
                {/* Discount badge sobre */}
                {product.discount > 0 && (
                  <span className='absolute -top-1.5 -right-1.5 z-10 text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-full'>
                    -{product.discount}%
                  </span>
                )}

                <div className='w-[90px] h-[90px] rounded-xl overflow-hidden bg-gray-50 border border-gray-100'>
                  <img
                    src={product.images?.[0]?.url || product.images?.[0]}
                    alt={product.name}
                    className='w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-400'
                    loading='lazy'
                  />
                </div>
              </div>

              {/* Infos */}
              <div className='flex flex-col gap-1.5 flex-1 min-w-0 text-[13px] text-gray-700 group-hover:text-gray-900 transition-colors leading-snug'>
                <TruncateText className='' text={product.name} maxLength={30} />

                {/* Rating */}
                <div className='flex items-center gap-1'>
                  <Ratings ratings={product.rating} />
                </div>

                {/* Price */}
                <div className='flex items-baseline gap-2 flex-wrap'>
                  <span className='text-sm font-bold text-gray-900'>
                    {finalPrice} {devise}
                  </span>
                  {product.discount > 0 && (
                    <span className='text-xs text-gray-400 line-through'>
                      {product.price} {devise}
                    </span>
                  )}
                </div>

                {/* Tags sobres */}
                <div className='flex flex-wrap gap-1.5'>
                  {product.stock > 0 ? (
                    <span className='text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full'>
                      {t("In Stock")}: {product.stock}
                    </span>
                  ) : (
                    <span className='text-[10px] text-red-400 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full'>
                      {t("Out of Stock")}
                    </span>
                  )}
                  {product.deliveryType === "free" && (
                    <span className='inline-flex items-center gap-0.5 text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full'>
                      <FaGift size={8} />
                      {t("Free delivery")}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default RecommendedProducts;
